import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building, Building2, Smartphone, Users, DollarSign, Activity, CreditCard } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import StatCard from '../../components/common/StatCard';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAdminStore } from '../../stores/adminStore';
import { api } from '../../services/api';
import type { DashboardStats, RecentActivity } from '../../types/admin';

interface DashboardAnalytics {
  growth: Array<{ key: string; label: string; tenants: number; applications: number }>;
  planMix: Array<{ tier: string; count: number }>;
}

const PIE_COLORS = ['#fe87a4', '#6d28d9', '#f24e78', '#7c3aed', '#fda4b8', '#a78bfa', '#8c2b4b'];

export default function Overview() {
  const { setStats } = useAdminStore();

  // Live dashboard: stats + activity auto-refresh every 15s
  const { data: stats, isLoading: statsLoading, dataUpdatedAt } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.get('/api/admin/stats'),
    refetchInterval: 15_000,
  });

  const { data: analytics } = useQuery<DashboardAnalytics>({
    queryKey: ['dashboard-analytics'],
    queryFn: () => api.get('/api/admin/dashboard'),
    refetchInterval: 60_000,
  });

  const { data: activities, isLoading: activitiesLoading } = useQuery<RecentActivity[]>({
    queryKey: ['recent-activities'],
    queryFn: () => api.get('/api/admin/activities/recent'),
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (stats) setStats(stats);
  }, [stats, setStats]);

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const planMix = analytics?.planMix || [];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-gray-600">Platform overview — tenants, applications, and revenue</p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-100 text-green-800 text-xs font-semibold">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          Live · updated {new Date(dataUpdatedAt).toLocaleTimeString()}
        </span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard title="Total Agencies" value={stats?.totalAgencies || 0} icon={Building} trend={stats?.agenciesTrend} />
        <StatCard title="Total Tenants" value={stats?.totalTenants || 0} icon={Building2} trend={stats?.tenantsTrend} />
        <StatCard title="Applications" value={stats?.totalApplications || 0} icon={Smartphone} trend={stats?.applicationsTrend} />
        <StatCard title="Active Users" value={stats?.activeUsers || 0} icon={Users} trend={stats?.usersTrend} />
        <StatCard title="Monthly Recurring Revenue" value={`$${(stats?.mrr || 0).toLocaleString()}`} icon={DollarSign} trend={stats?.mrrTrend} />
        <StatCard title="Active Subscriptions" value={stats?.activeSubscriptions || 0} icon={Activity} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Growth */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Growth (last 6 months)</h2>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={analytics?.growth || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gTenants" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fe87a4" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#fe87a4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gApps" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6d28d9" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#6d28d9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6b7280' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="tenants" name="New Tenants" stroke="#f24e78" fill="url(#gTenants)" strokeWidth={2} />
                <Area type="monotone" dataKey="applications" name="New Applications" stroke="#6d28d9" fill="url(#gApps)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Plan mix */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Active Plans by Tier</h2>
          {planMix.length === 0 ? (
            <div className="flex items-center justify-center h-[280px] text-sm text-gray-500">No active subscriptions</div>
          ) : (
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={planMix} dataKey="count" nameKey="tier" cx="50%" cy="50%" outerRadius={90} label={(e: any) => `${e.tier} (${e.count})`}>
                    {planMix.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity + Quick links */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Recent Activity</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {activitiesLoading ? (
              <div className="p-12"><LoadingSpinner /></div>
            ) : activities && activities.length > 0 ? (
              activities.map((activity) => (
                <div key={activity.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Activity className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                      <p className="text-sm text-gray-600">{activity.description}</p>
                      <p className="text-xs text-gray-500 mt-1">{new Date(activity.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-12 text-center text-gray-600">No recent activity</div>
            )}
          </div>
        </div>

        {/* Quick links */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Quick Links</h2>
          <div className="space-y-2">
            {[
              { to: '/tenants', icon: Building2, label: 'Manage Tenants' },
              { to: '/applications', icon: Smartphone, label: 'Applications' },
              { to: '/users', icon: Users, label: 'Users & Roles' },
              { to: '/billing', icon: CreditCard, label: 'Billing & Subscriptions' },
              { to: '/system', icon: Activity, label: 'System Health' },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors">
                <l.icon className="w-5 h-5 text-primary-600" />
                <span className="font-medium text-gray-800">{l.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
